(() => {
  const rootSelector = '.rvclimate';

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const initializeGallery = (root) => {
    const mainImage = root.querySelector('.rvclimate__gallery-main img');
    if (!mainImage) return;

    root.querySelectorAll('[data-rvclimate-thumbnail]').forEach((button) => {
      button.addEventListener('click', () => {
        mainImage.src = button.dataset.imageSrc;
        mainImage.srcset = button.dataset.imageSrcset || '';
        mainImage.alt = button.dataset.imageAlt || '';
        root.querySelectorAll('[data-rvclimate-thumbnail]').forEach((item) => item.setAttribute('aria-current', 'false'));
        button.setAttribute('aria-current', 'true');
      });
    });
  };

  const parseJson = (element, fallback) => {
    if (!element) return fallback;
    try { return JSON.parse(element.textContent); } catch (error) { return fallback; }
  };

  const normalize = (value) => String(value || '').trim().toLowerCase();

  const fulfillmentBundleIds = Object.freeze({
    white_ductless: 'TC15-W-DL',
    white_ducted: 'TC15-W-DT',
    black_ductless: 'TC15-B-DL',
    black_ducted: 'TC15-B-DT'
  });

  const buildFulfillmentProperties = (selection) => {
    if (!selection?.finish?.value || !selection?.assembly?.value) return null;
    const configurationKey = `${selection.finish.value}_${selection.assembly.value}`;
    const bundleId = fulfillmentBundleIds[configurationKey];
    if (!bundleId) return null;
    return {
      Configuration: `${selection.finish.label.toUpperCase()} ROOFTOP + ${selection.assembly.label.toUpperCase()} CEILING ASSEMBLY`,
      'Rooftop Finish': selection.finish.label,
      'Rooftop Unit SKU': selection.finish.model,
      'Ceiling Configuration': selection.assembly.label,
      'Ceiling Assembly SKU': selection.assembly.model,
      'Included Wireless Remote': '1 × MRDRC',
      'Included Wired Controller': '1 × MRDCW',
      'Bundle ID': bundleId
    };
  };

  const buildCartItemsWithFulfillmentProperties = (items, selection) => {
    const fulfillmentProperties = buildFulfillmentProperties(selection);
    if (!fulfillmentProperties || !Array.isArray(items) || !items.length) return [];
    return items.map((item, index) => {
      if (index !== 0) return item;
      return {
        ...item,
        properties: {
          ...(item.properties || {}),
          ...fulfillmentProperties
        }
      };
    });
  };

  const updateConfigurationSummaries = (selection) => {
    const rooftopText = selection.finish
      ? `${selection.finish.model} — ${selection.finish.label}`
      : 'Select White or Black';
    const assemblyText = selection.assembly
      ? `${selection.assembly.model} — ${selection.assembly.label}`
      : 'Select Ductless or Ducted';

    document.querySelectorAll('[data-rvclimate-summary-rooftop]').forEach((item) => { item.textContent = rooftopText; });
    document.querySelectorAll('[data-rvclimate-summary-assembly]').forEach((item) => { item.textContent = assemblyText; });
    document.querySelectorAll('[data-rvclimate-included-rooftop-model]').forEach((item) => { item.textContent = selection.finish?.model || 'Choose a finish above'; });
    document.querySelectorAll('[data-rvclimate-included-rooftop-label]').forEach((item) => { item.textContent = selection.finish?.label || 'Selected rooftop finish'; });
    document.querySelectorAll('[data-rvclimate-included-assembly-model]').forEach((item) => { item.textContent = selection.assembly?.model || 'Choose a ceiling configuration above'; });
    document.querySelectorAll('[data-rvclimate-included-assembly-label]').forEach((item) => { item.textContent = selection.assembly?.label || 'Selected ceiling assembly'; });
    document.querySelectorAll('[data-rvclimate-included-rooftop-image]').forEach((image) => {
      if (!selection.finish?.imageSrc) return;
      image.src = selection.finish.imageSrc;
      image.srcset = '';
      image.alt = selection.finish.imageAlt || 'Selected TravelCool rooftop unit';
    });
    document.querySelectorAll('[data-rvclimate-included-assembly-image]').forEach((image) => {
      if (!selection.assembly?.imageSrc) return;
      image.src = selection.assembly.imageSrc;
      image.srcset = '';
      image.alt = selection.assembly.imageAlt || 'Selected TravelCool ceiling assembly';
    });
  };

  const initializeConfigurator = (root) => {
    const form = root.querySelector('[data-rvclimate-configurator]');
    if (!form) return;

    const mode = form.dataset.configurationMode || 'unconfigured';
    const variants = parseJson(root.querySelector('[data-rvclimate-variants]'), []);
    const productOptions = parseJson(root.querySelector('[data-rvclimate-product-options]'), []);
    const configurationMap = parseJson(root.querySelector('[data-rvclimate-configuration-map]'), {});
    const componentProducts = parseJson(root.querySelector('[data-rvclimate-component-products]'), {});
    const hiddenVariant = form.querySelector('[data-rvclimate-selected-variant]');
    const addButton = form.querySelector('[data-rvclimate-add]');
    const quantityInput = form.querySelector('[data-rvclimate-quantity]');
    const mobileBuy = root.querySelector('[data-rvclimate-mobile-buy]');
    const stickyAdd = root.querySelector('[data-rvclimate-sticky-add]');
    const stickyPrice = root.querySelector('[data-rvclimate-sticky-price]');
    const message = form.querySelector('[data-rvclimate-configuration-message]');
    const price = root.querySelector('[data-rvclimate-current-price]');
    const comparePrice = root.querySelector('[data-rvclimate-compare-price]');
    const availability = root.querySelector('[data-rvclimate-availability]');
    const selectedModel = root.querySelector('[data-rvclimate-selected-model]');
    const mainImage = root.querySelector('.rvclimate__gallery-main img');
    const currency = root.dataset.currency || 'USD';
    const selection = { finish: null, assembly: null, cartItems: [], valid: false };

    const setMessage = (text, state = '') => {
      if (!message) return;
      message.textContent = text;
      message.dataset.state = state;
    };

    const setAvailability = (available, text) => {
      if (!availability) return;
      availability.dataset.available = String(available);
      availability.textContent = text;
    };

    const setPrice = (current, compareAt = null) => {
      if (price && current) price.textContent = formatMoney(current, currency);
      if (stickyPrice && current) stickyPrice.textContent = formatMoney(current, currency);
      if (!comparePrice) return;
      const showCompare = compareAt && compareAt > current;
      comparePrice.hidden = !showCompare;
      comparePrice.textContent = showCompare ? formatMoney(compareAt, currency) : '';
    };

    const exposeResolvedReferences = (resolved) => {
      root.dataset.resolvedProductId = resolved?.productId ? String(resolved.productId) : '';
      root.dataset.resolvedVariantId = resolved?.variantId ? String(resolved.variantId) : '';
    };

    const resolveVariantConfiguration = () => {
      const finishIndex = productOptions.findIndex((option) => normalize(option) === normalize(form.dataset.finishOptionName));
      const ceilingIndex = productOptions.findIndex((option) => normalize(option) === normalize(form.dataset.ceilingOptionName));
      if (finishIndex < 0 || ceilingIndex < 0) return null;

      return variants.find((variant) => (
        normalize(variant.options?.[finishIndex]) === normalize(selection.finish.label)
        && normalize(variant.options?.[ceilingIndex]) === normalize(selection.assembly.label)
      )) || null;
    };

    const resolveMappedConfiguration = () => {
      const key = `${selection.finish.value}_${selection.assembly.value}`;
      const entry = configurationMap?.[key];
      if (!entry) return null;
      const rawItems = Array.isArray(entry.items) ? entry.items : (entry.variant_id ? [{ id: entry.variant_id }] : []);
      const items = rawItems.filter((item) => item?.id).map((item) => ({ id: Number(item.id), quantity: 1 }));
      if (!items.length) return null;
      return {
        items,
        available: entry.available !== false,
        price: Number(entry.total_price) || null,
        compareAt: Number(entry.compare_at_price) || null,
        productId: entry.product_id || null,
        variantId: entry.variant_id || items[0]?.id || null,
        previewOnly: entry.preview_only === true
      };
    };

    const resolveComponentConfiguration = () => {
      const rooftop = componentProducts?.[selection.finish.value];
      const assembly = componentProducts?.[selection.assembly.value];
      if (!rooftop?.id || !assembly?.id) return null;
      return {
        items: [{ id: Number(rooftop.id), quantity: 1 }, { id: Number(assembly.id), quantity: 1 }],
        available: rooftop.available !== false && assembly.available !== false,
        price: Number(rooftop.price || 0) + Number(assembly.price || 0)
      };
    };

    const updateResolvedConfiguration = () => {
      selection.valid = false;
      selection.cartItems = [];
      exposeResolvedReferences(null);
      if (hiddenVariant) hiddenVariant.value = '';
      if (addButton) {
        addButton.disabled = true;
        addButton.textContent = addButton.dataset.addLabel;
      }
      if (stickyAdd) stickyAdd.disabled = true;

      if (!selection.finish || !selection.assembly) {
        setMessage('Choose one rooftop finish and one ceiling assembly.');
        setAvailability(false, 'Choose both options');
        return;
      }

      if (mode === 'unconfigured') {
        const resolved = resolveMappedConfiguration();
        if (!resolved) {
          setMessage('This preview configuration is missing its Shopify product reference.', 'error');
          setAvailability(false, 'Configuration unavailable');
          return;
        }
        exposeResolvedReferences(resolved);
        setPrice(resolved.price, resolved.compareAt);
        setMessage('This configuration is currently unavailable.', 'notice');
        setAvailability(false, 'Configuration unavailable');
        return;
      }

      if (mode === 'variant_options') {
        const variant = resolveVariantConfiguration();
        if (!variant) {
          setMessage('This combination is not mapped to a Shopify variant. Review the TravelCool product options.', 'error');
          setAvailability(false, 'Configuration unavailable');
          return;
        }
        if (hiddenVariant) hiddenVariant.value = variant.id;
        setPrice(variant.price, variant.compare_at_price);
        if (!variant.available) {
          setMessage('This complete system configuration is currently unavailable.', 'error');
          setAvailability(false, availability?.dataset.soldOut || 'Configuration unavailable');
          if (addButton) addButton.textContent = addButton.dataset.soldOutLabel;
          return;
        }
        selection.valid = true;
        selection.cartItems = [{ id: Number(variant.id), quantity: 1 }];
      } else {
        const resolved = mode === 'component_products' ? resolveComponentConfiguration() : resolveMappedConfiguration();
        if (!resolved) {
          setMessage('This combination has not been mapped to purchasable Shopify components.', 'error');
          setAvailability(false, 'Configuration unavailable');
          return;
        }
        if (resolved.previewOnly) {
          setMessage('This Draft preview map cannot be used for purchasing.', 'error');
          setAvailability(false, 'Configuration unavailable');
          return;
        }
        exposeResolvedReferences(resolved);
        if (hiddenVariant && resolved.variantId) hiddenVariant.value = resolved.variantId;
        setPrice(resolved.price, resolved.compareAt);
        if (!resolved.available) {
          setMessage('One or more required components are currently unavailable.', 'error');
          setAvailability(false, availability?.dataset.soldOut || 'Configuration unavailable');
          if (addButton) addButton.textContent = addButton.dataset.soldOutLabel;
          return;
        }
        selection.valid = true;
        selection.cartItems = resolved.items;
      }

      if (addButton) {
        addButton.disabled = false;
        addButton.textContent = addButton.dataset.addLabel;
      }
      if (stickyAdd) {
        stickyAdd.disabled = false;
        stickyAdd.textContent = addButton?.dataset.addLabel || 'Add complete system';
      }
      if (mobileBuy) mobileBuy.hidden = false;
      setAvailability(true, availability?.dataset.inStock || 'Complete system available');
      setMessage('Complete system selected. The MRDRC wireless remote and MRDCW wired wall controller are included.', 'success');
    };

    form.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.addEventListener('change', () => {
        const choice = { value: input.value, label: input.dataset.label, model: input.dataset.model, imageSrc: input.dataset.imageSrc, imageAlt: input.dataset.imageAlt };
        if (input.name === 'rv-finish') {
          selection.finish = choice;
          if (selectedModel) selectedModel.textContent = `Rooftop model ${choice.model} — ${choice.label}`;
          if (mainImage && input.dataset.imageSrc) {
            mainImage.src = input.dataset.imageSrc;
            mainImage.srcset = '';
            mainImage.alt = input.dataset.imageAlt || `${choice.label} TravelCool rooftop unit`;
          }
        } else if (input.name === 'rv-assembly') {
          selection.assembly = choice;
        }
        updateConfigurationSummaries(selection);
        updateResolvedConfiguration();
      });
    });

    form.addEventListener('submit', async (event) => {
      if (!selection.valid || mode === 'unconfigured') {
        event.preventDefault();
        event.stopImmediatePropagation();
        setMessage('Choose a valid complete system before adding to cart.', 'error');
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      addButton.disabled = true;
      addButton.setAttribute('aria-busy', 'true');
      addButton.textContent = 'Adding complete system…';
      if (stickyAdd) {
        stickyAdd.disabled = true;
        stickyAdd.textContent = 'Adding…';
      }
      try {
        const quantity = Math.max(1, Math.min(10, Number.parseInt(quantityInput?.value || '1', 10) || 1));
        const quantityItems = selection.cartItems.map((item) => ({ ...item, quantity }));
        const cartItems = buildCartItemsWithFulfillmentProperties(quantityItems, selection);
        if (!cartItems.length) throw new Error('Fulfillment configuration is incomplete');
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: cartItems })
        });
        if (!response.ok) throw new Error('Cart request failed');
        window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      } catch (error) {
        addButton.disabled = false;
        addButton.removeAttribute('aria-busy');
        addButton.textContent = addButton.dataset.addLabel;
        if (stickyAdd) {
          stickyAdd.disabled = false;
          stickyAdd.textContent = addButton.dataset.addLabel;
        }
        setMessage('The complete system could not be added. Please try again or contact support.', 'error');
      }
    }, true);

    stickyAdd?.addEventListener('click', () => form.requestSubmit());
    form.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    updateConfigurationSummaries(selection);
    updateResolvedConfiguration();
  };

  const initializeAutoSubmit = (root) => {
    root.querySelectorAll('[data-rvclimate-auto-submit]').forEach((control) => {
      control.addEventListener('change', () => control.form?.requestSubmit());
    });
  };

  const initializeIntegerFormatting = (root) => {
    root.querySelectorAll('[data-rvclimate-integer]').forEach((item) => {
      const value = Number(String(item.textContent || '').replace(/,/g, '').trim());
      if (Number.isFinite(value)) item.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-US').format(value);
    });
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll(rootSelector).forEach((root) => {
      if (root.dataset.rvclimateReady === 'true') return;
      root.dataset.rvclimateReady = 'true';
      initializeGallery(root);
      initializeConfigurator(root);
      initializeAutoSubmit(root);
      initializeIntegerFormatting(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
